import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchServerDetail } from "@/lib/api";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import SectionHeader from "@/components/SectionHeader";
import EventCard from "@/components/EventCard";
import UserCard from "@/components/UserCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Users, Trophy, Sparkles, Copy, Check, Zap, ShieldCheck, Server as ServerIcon,
  Calendar, Image as ImageIcon, ShoppingBag, MessageSquare, ThumbsUp, BookOpen,
  Newspaper, HelpCircle, ChevronDown, ChevronUp, ExternalLink, ListChecks, Info,
} from "lucide-react";

function useCopy() {
  const [copied, setCopied] = useState(false);
  return {
    copied,
    copy: async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true); sfx.copy();
        toast.success("Copied");
        setTimeout(() => setCopied(false), 1500);
      } catch { /* ignore */ }
    },
  };
}

function FaqItem({ q, a, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl glass overflow-hidden" data-testid="server-faq-item">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <span className="font-semibold text-sm">{q}</span>
        {open ? <ChevronUp size={16} style={{ color }} /> : <ChevronDown size={16} className="text-white/50" />}
      </button>
      {open && a && <div className="px-4 pb-4 text-sm text-white/70 whitespace-pre-wrap">{a}</div>}
    </div>
  );
}

function Widget({ w, color }) {
  const cfg = w.config || {};
  if (w.type === "text") return (
    <div className="rounded-xl glass p-4" data-testid={`server-widget-text`}>
      {w.title && <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color }}>{w.title}</div>}
      <div className="text-sm text-white/80 whitespace-pre-wrap">{cfg.body || cfg.text || ""}</div>
    </div>
  );
  if (w.type === "link") return (
    <a href={cfg.url || "#"} target="_blank" rel="noreferrer" className="rounded-xl glass p-4 hover:border-[#FFBF00]/40 block" data-testid="server-widget-link">
      <div className="flex items-center gap-2 text-sm font-semibold"><ExternalLink size={14} style={{ color }} /> {w.title || cfg.url}</div>
      {cfg.description && <div className="mt-1 text-xs text-white/50">{cfg.description}</div>}
    </a>
  );
  if (w.type === "stat") return (
    <div className="rounded-xl glass p-4" data-testid="server-widget-stat">
      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>{w.title || "Stat"}</div>
      <div className="font-hero text-3xl mt-1">{cfg.value ?? "—"}</div>
      {cfg.subtitle && <div className="text-xs text-white/40 mt-1">{cfg.subtitle}</div>}
    </div>
  );
  if (w.type === "image") return (
    <a href={cfg.url || "#"} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden glass hover:border-[#FFBF00]/40" data-testid="server-widget-image">
      {cfg.src && <img src={cfg.src} alt="" className="w-full h-full object-cover" />}
      {w.title && <div className="p-3 text-sm font-semibold">{w.title}</div>}
    </a>
  );
  // Fallback: render title only
  return (
    <div className="rounded-xl glass p-4">
      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>{w.type}</div>
      {w.title && <div className="text-sm font-semibold mt-1">{w.title}</div>}
    </div>
  );
}

export default function ServerDetail() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const ipCopy = useCopy();

  useEffect(() => {
    fetchServerDetail(key).then(setData).catch(() => setData("notfound"));
  }, [key]);

  useEffect(() => {
    if (data && data !== "notfound") document.title = `${data.server.name} · LostDC Servers`;
    return () => { document.title = "LostDC Network"; };
  }, [data]);

  if (data === "notfound") return <div className="pt-40 text-center text-white/60" data-testid="server-notfound">Server not found.</div>;
  if (!data) return <div className="pt-40 text-center text-white/50" data-testid="loading-server">Loading server…</div>;

  const { server: s, live, events, builds, creators, staff, announcements, leaderboard, community } = data;
  const color = s.color || "#FFBF00";
  const banner = s.banner || s.image;
  const features = (s.features && s.features.length ? s.features : s.highlights) || [];
  const ipDisplay = s.port ? `${s.ip}:${s.port}` : s.ip;

  return (
    <div className="pb-24" data-testid={`page-server-${s.key}`}>
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          {banner && <img src={banner} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0"
               style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.92)), linear-gradient(135deg, ${color}22, transparent 60%)` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[11px] uppercase tracking-[0.3em]"
               style={{ borderColor: `${color}55` }} data-testid="server-status-pill">
            <span className={`w-2 h-2 rounded-full ${live.online ? "bg-green-500 pulse-dot" : "bg-red-500"}`} />
            <span style={{ color }}>{live.online ? "Server Online" : "Offline"}</span> · {s.status}
          </div>
          <h1 className="mt-6 font-hero text-6xl sm:text-7xl md:text-8xl leading-[0.9]" style={{ color }}>{s.name}</h1>
          {s.tagline && <p className="mt-3 text-lg sm:text-xl text-white/70 max-w-3xl">{s.tagline}</p>}
          {s.description && <p className="mt-3 text-sm text-white/60 max-w-3xl">{s.description}</p>}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={() => ipCopy.copy(ipDisplay)} data-testid="server-copy-ip"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-mono text-sm"
                    style={{ backgroundColor: color, color: "#000" }}>
              <span>{ipDisplay}</span>
              {ipCopy.copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass" data-testid="server-online-count">
              <Users size={14} style={{ color }} />
              <span className="font-mono text-sm">{live.count} / {s.max_players} online</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass">
              <Zap size={14} style={{ color }} />
              <span className="font-mono text-sm">Java · {s.version}</span>
            </div>
          </div>

          {/* Link row */}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest">
            {s.store_url && (
              <a href={s.store_url} target="_blank" rel="noreferrer" data-testid="server-link-store"
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#FFBF00]/40">
                <ShoppingBag size={12} style={{ color }} /> Store
              </a>
            )}
            {s.discord_url && (
              <a href={s.discord_url} target="_blank" rel="noreferrer" data-testid="server-link-discord"
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#7289DA]/40">
                <MessageSquare size={12} style={{ color: "#7289DA" }} /> Discord
              </a>
            )}
            {(s.vote_urls || []).map((v, i) => (
              <a key={`vote-${i}`} href={v} target="_blank" rel="noreferrer" data-testid={`server-link-vote-${i}`}
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-emerald-400/40">
                <ThumbsUp size={12} className="text-emerald-400" /> Vote {(s.vote_urls || []).length > 1 ? `#${i + 1}` : ""}
              </a>
            ))}
            {community && (
              <Link to={`/communities/${community.slug}`} data-testid="server-link-community"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#FFBF00]/40">
                <Users size={12} style={{ color }} /> Discussion ({community.member_count})
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Announcements banner */}
      {announcements?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
          <div className="rounded-2xl glass p-4 flex flex-wrap gap-4 items-center">
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>Latest</div>
            <div className="text-sm text-white/80">{announcements[0].title}</div>
          </div>
        </section>
      )}

      {/* Rich markdown body */}
      {s.content_md && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-content-md">
          <SectionHeader eyebrow="About" title={`Inside ${s.name}`} />
          <div className="rounded-2xl glass p-6 prose prose-invert max-w-none text-sm text-white/80">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.content_md}</ReactMarkdown>
          </div>
        </section>
      )}

      {/* Features */}
      {features.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Features" title={`What makes ${s.name} unique`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="server-features">
            {features.map((h, i) => (
              <div key={`hl-${i}-${h}`} className="rounded-xl glass p-5">
                <Sparkles size={16} style={{ color }} className="mb-3" />
                <div className="font-semibold">{h}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      {(s.gallery || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-gallery">
          <SectionHeader eyebrow="Gallery" title="Screenshots" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {s.gallery.map((src, i) => (
              <a key={`g-${i}`} href={src} target="_blank" rel="noreferrer" data-testid={`server-gallery-${i}`}
                 className="block aspect-video rounded-xl overflow-hidden glass group hover:border-[#FFBF00]/40">
                <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Rules */}
      {(s.rules || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-rules">
          <SectionHeader eyebrow="Rules" title="Play Fair. Play Legendary." />
          <div className="rounded-2xl glass p-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={14} style={{ color }} />
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>Server-specific rules</div>
            </div>
            <ol className="space-y-2 text-sm text-white/80 list-decimal list-inside">
              {s.rules.map((r, i) => <li key={`rule-${i}`}>{r}</li>)}
            </ol>
          </div>
        </section>
      )}

      {/* Live players */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
        <SectionHeader eyebrow="Live Now" title="Who's Playing" subtitle="Real-time roster on this server." />
        {live.players?.length === 0 ? (
          <div className="rounded-2xl glass p-8 text-center text-white/50 text-sm">No players online right now.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {live.players.map((p) => (
              <div key={p.username} className="flex items-center gap-2 px-3 py-2 rounded-full glass" data-testid={`live-player-${p.username}`}>
                <img src={p.avatar} alt="" className="w-6 h-6 rounded" />
                <span className="text-sm font-mono">{p.username}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Leaderboard */}
      {leaderboard?.entries?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Top Players" title="Server Leaderboard" />
          <div className="grid md:grid-cols-2 gap-3">
            {leaderboard.entries.slice(0, 8).map((e) => (
              <div key={e.rank} className="flex items-center gap-4 p-3 rounded-lg glass">
                <div className="font-hero text-2xl w-10" style={{ color }}>#{e.rank}</div>
                <img src={`https://mc-heads.net/avatar/${e.username}/64`} alt="" className="w-9 h-9 rounded" loading="lazy" />
                <div className="flex-1 font-semibold">{e.username}</div>
                <div className="font-mono" style={{ color }}>{e.value.toLocaleString("en-IN")} <span className="text-white/40 text-xs">{e.unit}</span></div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Link to="/leaderboards" className="text-xs uppercase tracking-widest hover:underline" style={{ color }}>Full leaderboards →</Link>
          </div>
        </section>
      )}

      {/* News */}
      {(s.news || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-news">
          <SectionHeader eyebrow="News" title="Latest updates" />
          <div className="grid md:grid-cols-2 gap-4">
            {s.news.map((n, i) => (
              <article key={`n-${i}`} className="rounded-2xl glass overflow-hidden" data-testid={`server-news-${i}`}>
                {n.image && <img src={n.image} alt="" className="w-full aspect-video object-cover" />}
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Newspaper size={11} /> {n.date}</div>
                  <div className="font-hero text-lg mt-1">{n.title}</div>
                  {n.body && <div className="mt-2 text-sm text-white/70 whitespace-pre-wrap line-clamp-4">{n.body}</div>}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Changelog */}
      {(s.changelog || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-changelog">
          <SectionHeader eyebrow="Changelog" title="What's new & recent updates" />
          <div className="space-y-3">
            {s.changelog.map((c, i) => (
              <div key={`cl-${i}`} className="rounded-xl glass p-4" data-testid={`server-changelog-${i}`}>
                <div className="flex items-center gap-3">
                  <BookOpen size={14} style={{ color }} />
                  <div className="font-semibold">{c.title}</div>
                  <div className="ml-auto text-[10px] uppercase tracking-widest text-white/40">{c.date}</div>
                </div>
                {c.notes && Array.isArray(c.notes) && c.notes.length > 0 && (
                  <ul className="mt-2 pl-6 space-y-1 text-sm text-white/70 list-disc">
                    {c.notes.map((line, j) => <li key={`cl-${i}-${j}`}>{line}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {(s.faq || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-faq">
          <SectionHeader eyebrow="FAQ" title="Common questions" />
          <div className="grid md:grid-cols-2 gap-3">
            {s.faq.map((f, i) => <FaqItem key={`f-${i}`} q={f.q || f.question} a={f.a || f.answer} color={color} />)}
          </div>
        </section>
      )}

      {/* Widgets (admin-defined custom content) */}
      {(s.widgets || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-widgets">
          <SectionHeader eyebrow="More" title="Server widgets" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {s.widgets.map((w, i) => <Widget key={`w-${i}`} w={w} color={color} />)}
          </div>
        </section>
      )}

      {/* Related builds */}
      {builds?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Community" title="Builds on this server" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {builds.map((b) => (
              <Link key={b.id} to="/builds" className="rounded-xl overflow-hidden border border-white/10 group hover:border-[#FFBF00]/40">
                {(b.images?.[0] || b.image) && <img src={b.images?.[0] || b.image} alt="" className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />}
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-1">{b.title}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-widest">{b.category}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured creators */}
      {creators?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Creators" title="Creators who love this server" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {creators.map((c) => <UserCard key={c.id} user={c} />)}
          </div>
        </section>
      )}

      {/* Events */}
      {events?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Events" title="Coming up on this server" />
          <div className="grid md:grid-cols-2 gap-6">
            {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        </section>
      )}

      {/* Staff */}
      {staff?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Team" title="Server Staff" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {staff.map((m) => (
              <div key={m.id || m.name} className="rounded-xl glass p-4 flex items-center gap-3">
                {m.avatar && <img src={m.avatar} alt="" className="w-12 h-12 rounded-lg" />}
                <div>
                  <div className="font-semibold">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How to join */}
      {s.connection_steps?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="How to Join" title={`Connect to ${s.name}`} />
          <ol className="rounded-2xl glass p-6 space-y-3 text-sm text-white/80 list-decimal list-inside" data-testid="server-connection-steps">
            {s.connection_steps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </section>
      )}
    </div>
  );
}
